import type {
  InterventionFollowupFormValues,
  InterventionFollowupFormProps,
} from "./intervention-followup-form.type";

import { Form } from "antd";

import { useInterventionFollowup } from "@/entities/intervention";
import { useFormValidation } from "@/modules/form-validation";
import { useModelNavigate } from "@/modules/model";
import { dateAddedMonths, format, toDate } from "@/utilities/date";

export const useInterventionFollowupForm = ({
  intervention,
}: InterventionFollowupFormProps) => {
  const { navigateToItem } = useModelNavigate();
  const { useForm } = Form;

  const { mutate, isPending, isError, error } = useInterventionFollowup({
    id: intervention.id,
    onSuccess: (newItem) => navigateToItem("intervention", newItem),
  });

  const [form] = useForm<InterventionFollowupFormValues>();
  const formId = `intervention-followup-form-${intervention.id}`;
  const followUpName =
    intervention.followup?.original?.name ?? intervention.name;

  const interventionDate =
    intervention.scheduled_date ?? intervention.application_date;
  // Seems to be included in SHOW and LIST endpoints without `includes` -> TODO verify DEV-7902 & IMP-1349
  const studyFrequency = intervention.asset?.study_frequency_in_month ?? 12;
  const scheduledDate = toDate(
    dateAddedMonths(studyFrequency, interventionDate)
  );
  const initialValues = {
    name: `${followUpName} (${format(scheduledDate)})`,
    scheduled_date: scheduledDate,
    duplicate_completed_observations: false,
  };

  const { validationErrors, validation } = useFormValidation(isError, error, [
    "name",
    "scheduled_date",
    "duplicate_completed_observations",
  ]);

  const onFormSubmit = ({
    name,
    scheduled_date,
    duplicate_completed_observations,
  }: InterventionFollowupFormValues) => {
    mutate({
      name,
      scheduled_date: format(scheduled_date),
      duplicate_completed_observations,
    });
  };

  return {
    form,
    formId,
    initialValues,
    onFormSubmit,
    isPending,
    isError,
    validation,
    validationErrors,
  };
};
